import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Globe, AlertCircle, CheckCircle, XCircle, ExternalLink, Calendar } from 'lucide-react';
import { listEgressTargets } from '../lib/api/index.js';
import { format } from 'date-fns';

const DEST_STATE: Record<string, { label: string; cls: string }> = {
  deployed:   { label: 'Live',       cls: 'bg-success-50 text-success-700 border-success-200' },
  failed:     { label: 'Failed',     cls: 'bg-danger-50 text-danger-700 border-danger-200' },
  building:   { label: 'Building',   cls: 'bg-primary-50 text-primary-700 border-primary-200' },
  deploying:  { label: 'Deploying',  cls: 'bg-primary-50 text-primary-700 border-primary-200' },
  claimed:    { label: 'Queued',     cls: 'bg-grey-100 text-grey-700 border-grey-200' },
  superseded: { label: 'Superseded', cls: 'bg-grey-100 text-grey-600 border-grey-200' },
  shadow:     { label: 'Shadow',     cls: 'bg-warning-50 text-warning-700 border-warning-200' },
  _none:      { label: 'No builds',  cls: 'bg-grey-100 text-grey-600 border-grey-200' },
};

function timeAgo(ts: string): string {
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export function SitesPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['egress-targets'],
    queryFn: listEgressTargets,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="inline-block h-10 w-10 animate-spin rounded-full border-4 border-solid border-primary-500 border-r-transparent"></div>
          <p className="mt-6 text-grey-600 font-medium">Loading sites...</p>
        </div>
      </div>
    );
  }

  if (error || !data || data.items.length === 0) {
    return (
      <div className="space-y-8 max-w-7xl mx-auto px-4 py-6">
        <div className="bg-surface rounded-xl border border-grey-200 p-16 text-center shadow-md">
          <Globe className="h-16 w-16 text-grey-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-grey-900 mb-2">No Sites Configured</h2>
          <p className="text-base text-grey-600">
            Egress targets are configured via database migrations.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-7xl mx-auto px-4 py-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {data.items.map((target) => {
          const domain = target.config?.domain || target.config?.github_repo;
          const awaiting = target.awaiting_promotion ?? 0;
          const dests = target.destinations ?? [];
          // Worst-of the masthead's destinations. `behind` is not a fault —
          // it means content has moved on and a build is owed — so it reads
          // as a warning while `failed` reads as danger.
          const buildPill = ({
            failed:   { label: 'Build failed', cls: 'bg-danger-50 text-danger-700 border-danger-200' },
            building: { label: 'Building',     cls: 'bg-primary-50 text-primary-700 border-primary-200' },
            behind:   { label: 'Build owed',   cls: 'bg-warning-50 text-warning-700 border-warning-200' },
            deployed: { label: 'Live',         cls: 'bg-success-50 text-success-700 border-success-200' },
          } as const)[target.worst_state ?? ''] ?? null;
          return (
            <Link
              key={target.target_id}
              to={`/publishing/${target.target_id}`}
              className="bg-surface rounded-xl border border-grey-200 overflow-hidden hover:border-primary-500 hover:shadow-md transition-all"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Globe className="h-6 w-6 text-primary-600" />
                    <h3 className="text-lg font-bold text-grey-900">{target.target_name}</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium bg-grey-100 text-grey-600">
                      {target.target_type}
                    </span>
                    {/* Build state — the question the list could not answer.
                        Article counts change slowly and rarely matter; whether
                        a masthead is deploying, behind or failed changes
                        constantly and always does. Worst-of its destinations,
                        so one glance across the grid finds trouble. */}
                    {buildPill && (
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${buildPill.cls}`}>
                        {buildPill.label}
                      </span>
                    )}
                    {target.is_active ? (
                      <CheckCircle className="h-5 w-5 text-success-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-grey-400" />
                    )}
                  </div>
                </div>

                {domain && (
                  <div className="flex items-center space-x-2 text-sm text-grey-600 mb-4">
                    <ExternalLink className="h-4 w-4" />
                    <span className="truncate">{domain}</span>
                  </div>
                )}

                {/* Per-destination state — what is live, what is owed, is a
                    build running. Replaced a 4-up stat grid whose two biggest
                    numbers (Live / Test) were not comparable, and invited a
                    subtraction that meant nothing. */}
                <div className="pt-4 border-t border-grey-200">
                  {dests.length > 0 ? (
                    <div className="space-y-1.5">
                      {dests.map((d) => {
                        const isMain = d.destination_type === 'main_branch';
                        const st = DEST_STATE[d.state ?? ''] ?? DEST_STATE._none;
                        return (
                          <div key={d.destination_type} className="flex items-center gap-2 text-sm">
                            <span className="font-semibold text-grey-900 w-20 flex-shrink-0">
                              {isMain ? 'Production' : 'Preview'}
                            </span>
                            <span className="text-xs text-grey-500 flex-1 min-w-0 truncate">
                              {d.live_content_version !== null && (
                                <span className="tabular-nums">v{d.live_content_version} live</span>
                              )}
                              {d.behind_record && <span className="text-warning-700"> · build owed</span>}
                              {d.deployed_at && <span> · {timeAgo(d.deployed_at)}</span>}
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium flex-shrink-0 ${st.cls}`}>
                              {st.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-grey-500">No builds recorded yet.</p>
                  )}

                  <div className="mt-3 pt-3 border-t border-grey-100 flex items-center gap-2 text-xs text-grey-500 flex-wrap">
                    <span className="tabular-nums">{target.main_article_count.toLocaleString()} published</span>
                    <span>·</span>
                    <span className={awaiting > 0 ? 'text-warning-700 font-semibold' : ''}>
                      {awaiting} awaiting promotion
                    </span>
                    {target.last_published_at && (
                      <>
                        <span>·</span>
                        <span>last {format(new Date(target.last_published_at), 'MMM d')}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
